import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { api } from '../services/api';

interface Category {
  id: string;
  name: string;
  type: string;
  parentId?: string;
}

interface MappingRule {
  id: string;
  pattern: string;
  patternType: 'contains' | 'regex' | 'exact';
  action: 'suggest_category' | 'flag_split' | 'flag_review';
  categoryId?: string;
  targetMonthOffset?: number;
  priority: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

interface RuleForm {
  pattern: string;
  patternType: 'contains' | 'regex' | 'exact';
  action: 'suggest_category' | 'flag_split' | 'flag_review';
  categoryId: string;
  targetMonthOffset: number;
  priority: number;
  enabled: boolean;
}

const EMPTY_FORM: RuleForm = {
  pattern: '',
  patternType: 'contains',
  action: 'suggest_category',
  categoryId: '',
  targetMonthOffset: 0,
  priority: 10,
  enabled: true,
};

export default function RulesPage() {
  const [rules, setRules] = useState<MappingRule[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([
        api.get('/api/rules'),
        api.get('/api/categories'),
      ]);
      setRules(r as MappingRule[]);
      setCategories(c as Category[]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categoryName = (id?: string) => {
    if (!id) return '—';
    const cat = categories.find((c) => c.id === id);
    return cat?.name || id;
  };

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (rule: MappingRule) => {
    setEditingId(rule.id);
    setForm({
      pattern: rule.pattern,
      patternType: rule.patternType,
      action: rule.action,
      categoryId: rule.categoryId || '',
      targetMonthOffset: rule.targetMonthOffset || 0,
      priority: rule.priority,
      enabled: rule.enabled,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        categoryId: form.categoryId || undefined,
        targetMonthOffset: form.targetMonthOffset || undefined,
      };
      if (editingId) {
        await api.put(`/api/rules/${editingId}`, payload);
      } else {
        await api.post('/api/rules', payload);
      }
      setDialogOpen(false);
      await load();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/api/rules/${id}`);
      await load();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggle = async (rule: MappingRule) => {
    try {
      await api.put(`/api/rules/${rule.id}`, { enabled: !rule.enabled });
      await load();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const actionLabel = (action: string) => {
    switch (action) {
      case 'suggest_category': return 'Ehdota kategoriaa';
      case 'flag_split': return 'Merkitse jaettavaksi';
      case 'flag_review': return 'Merkitse tarkistettavaksi';
      default: return action;
    }
  };

  const patternTypeLabel = (pt: string) => {
    switch (pt) {
      case 'contains': return 'sisältää';
      case 'exact': return 'tarkka';
      case 'regex': return 'regex';
      default: return pt;
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>
          Luokittelusäännöt
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          Lisää sääntö
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {rules.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary">
              Ei sääntöjä. Lisää sääntöjä, jotta tuodut tapahtumat luokitellaan automaattisesti maksajan nimen perusteella.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Esimerkiksi: Jos maksajan nimi <strong>sisältää</strong> "prisma" → ehdota kategoriaa "Ruokaostokset".
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1}>
          {rules
            .sort((a, b) => a.priority - b.priority)
            .map((rule) => (
              <Card key={rule.id} sx={{ opacity: rule.enabled ? 1 : 0.5 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Chip
                        label={patternTypeLabel(rule.patternType)}
                        size="small"
                        variant="outlined"
                      />
                      <Typography fontWeight={600} fontFamily="monospace">
                        {rule.pattern}
                      </Typography>
                      <Typography color="text.secondary">→</Typography>
                      <Chip
                        label={rule.action === 'suggest_category'
                          ? categoryName(rule.categoryId)
                          : actionLabel(rule.action)}
                        size="small"
                        color={rule.action === 'suggest_category' ? 'primary' : 'warning'}
                      />
                      {rule.targetMonthOffset && rule.targetMonthOffset !== 0 && (
                        <Chip
                          label={rule.targetMonthOffset > 0 ? `+${rule.targetMonthOffset} kk` : `${rule.targetMonthOffset} kk`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Switch
                        size="small"
                        checked={rule.enabled}
                        onChange={() => handleToggle(rule)}
                      />
                      <IconButton size="small" onClick={() => openEdit(rule)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(rule.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
        </Stack>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Muokkaa sääntöä' : 'Uusi sääntö'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Hakuehto (maksajan nimi)"
              value={form.pattern}
              onChange={(e) => setForm({ ...form, pattern: e.target.value })}
              fullWidth
              placeholder="esim. prisma, k-market, hsl..."
              helperText="Vertaillaan maksajan nimeen (pienillä kirjaimilla)"
            />
            <FormControl fullWidth>
              <InputLabel>Hakutyyppi</InputLabel>
              <Select
                value={form.patternType}
                label="Hakutyyppi"
                onChange={(e) => setForm({ ...form, patternType: e.target.value as any })}
              >
                <MenuItem value="contains">Sisältää</MenuItem>
                <MenuItem value="exact">Tarkka osuma</MenuItem>
                <MenuItem value="regex">Regex</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Toiminto</InputLabel>
              <Select
                value={form.action}
                label="Toiminto"
                onChange={(e) => setForm({ ...form, action: e.target.value as any })}
              >
                <MenuItem value="suggest_category">Ehdota kategoriaa</MenuItem>
                <MenuItem value="flag_split">Merkitse jaettavaksi</MenuItem>
                <MenuItem value="flag_review">Merkitse tarkistettavaksi</MenuItem>
              </Select>
            </FormControl>
            {form.action === 'suggest_category' && (
              <FormControl fullWidth>
                <InputLabel>Kategoria</InputLabel>
                <Select
                  value={form.categoryId}
                  label="Kategoria"
                  onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.parentId ? '  └ ' : ''}{cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {form.action === 'suggest_category' && (
              <FormControl fullWidth>
                <InputLabel>Kohdista kuukauteen</InputLabel>
                <Select
                  value={form.targetMonthOffset}
                  label="Kohdista kuukauteen"
                  onChange={(e) => setForm({ ...form, targetMonthOffset: Number(e.target.value) })}
                >
                  <MenuItem value={-1}>Edellinen kuukausi</MenuItem>
                  <MenuItem value={0}>Tapahtumakuukausi (oletus)</MenuItem>
                  <MenuItem value={1}>Seuraava kuukausi</MenuItem>
                </Select>
              </FormControl>
            )}
            <TextField
              label="Prioriteetti"
              type="number"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
              helperText="Pienempi numero = korkeampi prioriteetti"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                />
              }
              label="Käytössä"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Peruuta</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.pattern}>
            {editingId ? 'Tallenna' : 'Lisää'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
